import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Megaphone, Plus, Search } from "lucide-react";

import {
  Badge,
  Panel,
  ProductStatusBadge,
  RowAction,
  RowActions,
  Table,
  Td,
  Tr,
} from "../components/desk.tsx";
import { Button, Empty, ErrorBlock, Input, Price, Spinner, currencySymbol, fen } from "../components/ui.tsx";
import { PhoneInput } from "../components/phone-input.tsx";
import { phoneProblem } from "@zx/contracts";
import { ReasonDialog } from "../components/reason-dialog.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { api } from "../api/client.ts";
import { toCents } from "../api/org-client.ts";

/**
 * ═══════════════════════════════════════════════════════════════
 *  后台 · 全站商品 / 价格规则 / 用户 / 公告 / 系统健康
 * ═══════════════════════════════════════════════════════════════
 *
 * 这五页补的都是「前台有这个能力、后台管不了」的窟窿：
 * - **全站商品**：以前后台只看得到「待审核」那一批，想下架一件在架商品没有入口。
 * - **价格规则 / 限价**：这个平台的价格模型本身，以前只能改数据库。
 * - **用户**：以前只有一个"按手机号搜人"，查到了也不能禁用。
 * - **公告**：站内消息只有系统自动发的，平台想说句话没地方说。
 * - **系统健康**：错误告警和备份的代码早就写好了，一直没接路由，等于不存在。
 */

// ═══════════════════════════ 全站商品 ═══════════════════════════

const PRODUCT_STATUSES = [
  { key: "", label: "全部" },
  { key: "active", label: "在架" },
  { key: "pending", label: "待审核" },
  { key: "offline", label: "已下架" },
  { key: "rejected", label: "已驳回" },
  { key: "draft", label: "草稿" },
];

interface AdminProduct {
  id: number;
  title: string;
  status: string;
  supplierOrgId: number;
  categoryId: number | null;
  minSupplyPrice: number;
  createdAt: string;
}

/**
 * ═══════════════════════════════════════════════════════════════
 *  给商品定平台价
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **老板的原话：「陈总管理自己店铺定价，商城后台对整个平台进行管理
 *    对商品定平台价」。** 所以这件事只在后台，商家表单里不出现 ——
 *    平台价是全站的天花板，不该由某一家商户去设。
 *
 * 平台价管两件事：
 * - **没归属的人（游客、自己注册的人）付的价** —— 前提是陈总的店没单独定价；
 * - **划线价** —— 有归属的人看到"吊牌 ¥238、你的价 ¥178"里划掉的那个数。
 *
 * 🔴 **自建商品最缺它。** 商家自己上架的货没人填过平台价，天花板就没了 ——
 *    真机上出现过"游客 ¥1100、东鹏下级的客户 ¥1331"，
 *    而老板说过「游客登录看着便宜，登录进入点进店铺更贵了，这是不现实的」。
 *
 * ⚠️ 改平台价**不动任何人的进货成本**（那是批发价那条线），所以不冻价、不发通知。
 */
interface AdminSku {
  id: number;
  specs: Record<string, string>;
  unit: string;
  stock: number;
  active: boolean;
  supplyPrice: number;
  listPrice: number | null;
}

/**
 * 审核通过 —— **同一步把平台价定下来**。
 *
 * 🔴 **老板的原话：「自建商品是需要审核的，审核是平台来审核，
 *    审核时就会有平台价，审核通过才会上架平台」。**
 *
 *    所以这里不是"通过"一个按钮就完事：平台价是全站的天花板，
 *    而**商家自建的商品表单里根本没有这个字段**（那是平台的事），
 *    审核这一步是它唯一的入口。缺一个规格服务端就不让上架。
 *
 * ⚠️ 供应商上架时填过「指导零售价」的，这里预填出来给审核员确认或调整；
 *    没填的留空 —— 空着不许通过，占位符里给一个建议值（供货价的 3 倍）。
 *    直接替他填上一个编出来的数，等于把这一步变回橡皮图章。
 */
export function ApproveWithPriceDialog({
  product,
  onClose,
  onDone,
}: {
  product: { id: number; title: string } | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [drafts, setDrafts] = useState<Record<number, string>>({});

  const q = useQuery({
    queryKey: ["admin", "product-skus", product?.id],
    queryFn: () => api.get<{ items: AdminSku[] }>(`/admin/products/${product!.id}/skus`),
    enabled: Boolean(product),
  });

  /**
   * 换商品要清草稿。
   *
   * 🔴 **不预填供应商填过的数。** 老板：「平台价格是平台设置的，需要平台管理员
   *    设置平台价格，另外不需要供应商建议售价」。预填出来的话，审核就变成了
   *    点一下「通过」的橡皮图章 —— 而平台价是**全站的价格天花板**
   *    （游客付它、所有人的划线价是它），它必须是平台自己的决定。
   */
  useEffect(() => {
    setDrafts({});
  }, [product?.id]);

  const approve = useMutation({
    mutationFn: (platformPrices: Record<number, number>) =>
      api.post(`/admin/review/products/${product!.id}/approve`, { platformPrices }),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });

  const active = (q.data?.items ?? []).filter((s) => s.active);
  const ready = active.length > 0 && active.every((s) => (toCents(drafts[s.id] ?? "") ?? 0) > 0);

  return (
    <Dialog open={Boolean(product)} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[640px]">
        <DialogHeader>
          <DialogTitle>审核通过 · {product?.title}</DialogTitle>
          <DialogDescription>
            通过之前要给每个规格定<strong className="text-foreground">平台价</strong>
            —— 全站的指导零售价，也是所有人的价格天花板：没有上级的人（游客）付它，
            有上级的人看到的划线价也是它。
            <br />
            ⚠️ 没有它，越往下游卖得越贵，<strong className="text-foreground">陌生人反而买得最便宜</strong>
            。这个数由平台定，不看供应商填了什么。
          </DialogDescription>
        </DialogHeader>

        {q.isPending ? (
          <Spinner compact />
        ) : q.error ? (
          <ErrorBlock error={q.error} />
        ) : (
          <div className="max-h-[50vh] space-y-2 overflow-y-auto">
            {active.map((s) => (
              <div
                key={s.id}
                className="flex items-center gap-3 rounded-lg border border-border px-4 py-2.5"
              >
                <span className="min-w-0 flex-1 text-[14px]">
                  {Object.values(s.specs).join(" · ") || "默认"}
                  <span className="ml-2 text-[12px] text-muted-foreground">
                    供货价 <Price cents={s.supplyPrice} />
                  </span>
                </span>
                {/*
                  ⚠️ 货币符号做成前缀，不在后面挂"元/件"：**"元"是写死的**，
                     而这个站的货币是后台「外观」里配的（CNY → ¥ / TWD → NT$）——
                     切到台币之后整站都换了符号，只有这一处还写着"元"。
                     跟审核列表里那一格是同一个写法。
                */}
                <div className="flex h-9 w-[140px] shrink-0 items-center rounded-lg border border-input bg-background pr-2 pl-2.5 focus-within:border-brand-500">
                  <span className="shrink-0 text-[13px] text-muted-foreground">
                    {currencySymbol()}
                  </span>
                  <input
                    inputMode="decimal"
                    className="min-w-0 flex-1 border-0 bg-transparent text-right text-[14px] tabular-nums outline-none"
                    value={drafts[s.id] ?? ""}
                    onChange={(e) => setDrafts((d) => ({ ...d, [s.id]: e.target.value }))}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
        {approve.error && <ErrorBlock error={approve.error} />}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            取消
          </Button>
          <Button
            disabled={!ready}
            loading={approve.isPending}
            onClick={() =>
              approve.mutate(
                Object.fromEntries(active.map((s) => [s.id, toCents(drafts[s.id] ?? "")!])),
              )
            }
          >
            定价并通过
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PlatformPriceDialog({
  product,
  onClose,
}: {
  product: AdminProduct | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [drafts, setDrafts] = useState<Record<number, string>>({});

  const q = useQuery({
    queryKey: ["admin", "product-skus", product?.id],
    queryFn: () => api.get<{ items: AdminSku[] }>(`/admin/products/${product!.id}/skus`),
    enabled: Boolean(product),
  });

  /** ⚠️ 每次换商品都把草稿清掉 —— 留着上一个商品的数字太容易按错。 */
  useEffect(() => {
    setDrafts({});
  }, [product?.id]);

  const save = useMutation({
    mutationFn: (v: { skuId: number; listPrice: number | null }) =>
      api.patch(`/admin/skus/${v.skuId}/platform-price`, { listPrice: v.listPrice }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "product-skus"] });
      /** ⚠️ 前台的价是另一个查询 —— 不作废的话去商城一看还是老价，会以为没保存上。 */
      void qc.invalidateQueries({ queryKey: ["products"] });
    },
  });

  return (
    <Dialog open={Boolean(product)} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[640px]">
        <DialogHeader>
          <DialogTitle>定平台价 · {product?.title}</DialogTitle>
          <DialogDescription>
            平台价是<strong className="text-foreground">全站的指导零售价</strong>：
            没有上级的人（游客、自己注册的人）付这个价，有上级的人看到的划线价也是它。
            <br />
            ⚠️ 它<strong className="text-foreground">不影响任何商家的进货成本</strong>
            —— 那是「给下级的批发价」，陈总在自己的商家端改。留空 = 这个规格没有平台价。
          </DialogDescription>
        </DialogHeader>

        {q.isPending ? (
          <Spinner compact />
        ) : q.error ? (
          <ErrorBlock error={q.error} />
        ) : (
          <div className="max-h-[50vh] space-y-2 overflow-y-auto">
            {q.data.items.map((s) => {
              const shown = drafts[s.id] ?? (s.listPrice === null ? "" : (s.listPrice / 100).toFixed(2));
              return (
                <div
                  key={s.id}
                  className="flex items-center gap-3 rounded-lg border border-border px-4 py-2.5"
                >
                  <span className="min-w-0 flex-1 text-[14px]">
                    {Object.values(s.specs).join(" · ") || "默认"}
                    <span className="ml-2 text-[12px] text-muted-foreground">
                      供货价 <Price cents={s.supplyPrice} />
                    </span>
                    {!s.active && (
                      <span className="ml-2 text-[12px] text-muted-foreground">已停用</span>
                    )}
                  </span>
                  <Input
                    inputMode="decimal"
                    placeholder="未设"
                    className="h-9 w-[120px] text-right"
                    value={shown}
                    onChange={(e) => setDrafts((d) => ({ ...d, [s.id]: e.target.value }))}
                  />
                  <span className="w-12 shrink-0 text-[12px] text-muted-foreground">
                    {currencySymbol()}
                  </span>
                  <Button
                    size="sm"
                    loading={save.isPending && save.variables?.skuId === s.id}
                    onClick={() => {
                      const raw = shown.trim();
                      /** ⚠️ 留空 = 取消平台价，跟"填 0"是两回事（0 会被服务端拒）。 */
                      save.mutate({ skuId: s.id, listPrice: raw === "" ? null : toCents(raw) });
                    }}
                  >
                    保存
                  </Button>
                </div>
              );
            })}
          </div>
        )}
        {save.error && <ErrorBlock error={save.error} />}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            关闭
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AllProductsTab() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const [keyword, setKeyword] = useState("");
  const [askOffline, setAskOffline] = useState<AdminProduct | null>(null);
  const [pricing, setPricing] = useState<AdminProduct | null>(null);

  const q = useQuery({
    queryKey: ["admin", "all-products", status, keyword],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (status) qs.set("status", status);
      if (keyword) qs.set("keyword", keyword);
      return api.get<{ items: AdminProduct[] }>(`/admin/products?${qs}`);
    },
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["admin"] });
    /** ⚠️ 前台那份也要作废，否则下架了前台还挂着卖。 */
    void qc.invalidateQueries({ queryKey: ["products"] });
  };

  const offline = useMutation({
    mutationFn: (v: { id: number; reason: string }) =>
      api.post(`/admin/review/products/${v.id}/offline`, { reason: v.reason }),
    onSuccess: () => {
      setAskOffline(null);
      invalidate();
    },
  });

  return (
    <>

      <div className="mb-4 flex items-center gap-4">
        <div className="flex gap-1.5">
          {PRODUCT_STATUSES.map((t) => (
            <button
              key={t.key || "all"}
              onClick={() => setStatus(t.key)}
              className={cn(
                "rounded-lg px-3 py-1.5 text-[13px] transition-colors",
                status === t.key
                  ? "bg-brand-600 text-white"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground",
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative ml-auto w-[280px]">
          <Search className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="h-9 pl-10"
            placeholder="搜商品名"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
        </div>
      </div>

      {q.isPending ? (
        <Spinner compact />
      ) : q.error ? (
        <ErrorBlock error={q.error} />
      ) : q.data.items.length === 0 ? (
        <Empty title="没有符合条件的商品" compact />
      ) : (
        <Table head={["商品", "供应商", "最低供货价", "状态", "上架时间", "操作"]}>
          {q.data.items.map((p) => (
            <Tr key={p.id}>
              <Td>{p.title}</Td>
              <Td className="text-muted-foreground">#{p.supplierOrgId}</Td>
              <Td className="tabular-nums">
                <Price cents={p.minSupplyPrice} />
              </Td>
              <Td>
                <ProductStatusBadge status={p.status} />
              </Td>
              <Td className="text-muted-foreground">
                {new Date(p.createdAt).toLocaleDateString("zh-CN")}
              </Td>
              <Td>
                <span className="flex items-center gap-3">
                  {/*
                    🔴 定平台价对**任何状态**的商品都开放：自建商品常常刚提交还没审，
                       等审完再想起来定平台价，中间那段时间它是没天花板的。
                  */}
                  <button
                    className="text-[12px] text-brand-600 hover:underline"
                    onClick={() => setPricing(p)}
                  >
                    定平台价
                  </button>
                  {p.status === "active" && (
                    <button
                      className="text-[12px] text-destructive hover:underline"
                      onClick={() => setAskOffline(p)}
                    >
                      强制下架
                    </button>
                  )}
                </span>
              </Td>
            </Tr>
          ))}
        </Table>
      )}

      <ReasonDialog
        open={Boolean(askOffline)}
        title={`强制下架「${askOffline?.title ?? ""}」`}
        description="下架后客户立刻看不到这件商品。已经下过的单不受影响，该发的货照发。理由会发给供应商。"
        confirmLabel="确认下架"
        danger
        loading={offline.isPending}
        onConfirm={(reason) => askOffline && offline.mutate({ id: askOffline.id, reason })}
        onClose={() => setAskOffline(null)}
      />
      {offline.error && <ErrorBlock error={offline.error} />}

      <PlatformPriceDialog product={pricing} onClose={() => setPricing(null)} />
    </>
  );
}

// ═══════════════════════════ 外观 ═══════════════════════════

/**
 * ═══════════════════════════════════════════════════════════════
 *  后台 · 外观（配色 / 字体 / 货币）
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **这三样是"整个站点长什么样"，所以由平台后台统一切，不是每个用户自己挑。**
 *    老板：「给网站设置几种常见配色，可以切换…这个是平台管理后台来切换」。
 *
 * 🔴 **配色必须是色卡，不能是下拉框。** 挑颜色靠看，不靠读名字 ——
 *    一个写着「ocean / forest / plum」的下拉框，运营得一个个点开看效果才知道
 *    自己选的是什么，那等于把选色变成了猜谜。
 *
 * ⚠️ 三个开关都**只影响显示**，一分钱都不动。货币尤其：
 *    它声明"库里的分是哪种货币的分"，切换**只换符号、不折算金额** ——
 *    折算意味着页面显示 NT$ 而实际按 CNY 扣钱，那是资金事故。
 */

const THEMES: { key: string; name: string; swatch: string; hint: string }[] = [
  { key: "terracotta", name: "赤陶", swatch: "oklch(0.55 0.13 52)", hint: "默认，暖木色，贴建材调性" },
  { key: "ocean", name: "海蓝", swatch: "oklch(0.55 0.15 252)", hint: "冷静、偏工具感" },
  { key: "forest", name: "松绿", swatch: "oklch(0.52 0.12 158)", hint: "环保 / 自然" },
  { key: "graphite", name: "石墨", swatch: "oklch(0.44 0.02 270)", hint: "近中性，最不抢眼" },
  { key: "plum", name: "梅红", swatch: "oklch(0.52 0.16 355)", hint: "促销感最强" },
];

const SCRIPTS: { key: string; name: string; hint: string }[] = [
  { key: "hans", name: "简体中文", hint: "默认" },
  { key: "hant", name: "繁體中文", hint: "字形转繁体，并把网络/默认这类词换成台湾说法" },
];

const CURRENCIES: { key: string; name: string; hint: string }[] = [
  { key: "CNY", name: "人民币 ¥", hint: "默认" },
  { key: "TWD", name: "新台币 NT$", hint: "只换符号，不折算金额" },
];

function Choice({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors",
        active
          ? "border-brand-600 bg-brand-50/60"
          : "border-border bg-background hover:border-brand-200",
      )}
    >
      {children}
    </button>
  );
}

interface Appearance {
  theme: string;
  script: string;
  currency: string;
}

export function AppearanceTab() {
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["admin", "settings", "appearance"],
    queryFn: () =>
      api.get<{ value: Appearance; updatedAt: string; isDefault: boolean }>(
        "/admin/settings/appearance",
      ),
  });

  const save = useMutation({
    mutationFn: (value: Appearance) =>
      api.put("/admin/settings/appearance", {
        value,
        /** ⚠️ isDefault = 库里还没这一行，这时没有乐观锁可带。 */
        expectedUpdatedAt: q.data?.isDefault ? null : q.data?.updatedAt,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "settings"] });
      /**
       * 🔴 **前台那份配置是另一个查询，必须一起作废。**
       *    不作废的话他在后台点完配色，切到商城一看还是老颜色，
       *    会以为没保存上然后反复点。
       */
      void qc.invalidateQueries({ queryKey: ["site-config"] });
    },
  });

  if (q.isPending) return <Spinner compact />;
  if (q.error) return <ErrorBlock error={q.error} />;

  const cur = q.data.value;
  const patch = (p: Partial<Appearance>) => save.mutate({ ...cur, ...p });

  return (
    <>

      <Panel title="配色" hint="换的是主色，布局和排版一点都不动。选完立刻生效，前台刷新即可看到。">
        <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 md:grid-cols-3 md:p-5">
          {THEMES.map((t) => (
            <Choice key={t.key} active={cur.theme === t.key} onClick={() => patch({ theme: t.key })}>
              <span
                className="size-9 shrink-0 rounded-full border border-black/10"
                style={{ background: t.swatch }}
              />
              <span className="min-w-0">
                <span className="block text-[14px]">{t.name}</span>
                <span className="block text-[12px] text-muted-foreground">{t.hint}</span>
              </span>
            </Choice>
          ))}
        </div>
      </Panel>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Panel title="字体" hint="简体 / 繁体。这是字形转换，不是翻译。">
          <div className="space-y-3 p-5">
            {SCRIPTS.map((s) => (
              <Choice
                key={s.key}
                active={cur.script === s.key}
                onClick={() => patch({ script: s.key })}
              >
                <span className="min-w-0">
                  <span className="block text-[14px]">{s.name}</span>
                  <span className="block text-[12px] text-muted-foreground">{s.hint}</span>
                </span>
              </Choice>
            ))}
            {/*
              ⚠️ 必须说清"切回简体要刷新" —— 一简对多繁不可逆
                 （「後」和「后」都要变回「后」），所以只停止继续转，不逆转已转的字。
                 不写这一句的话，运营切回简体发现页面还是繁体，会以为开关坏了。
            */}
            <p className="text-[12px] text-muted-foreground">
              ⚠️ 从繁体切回简体后，已经打开的页面要
              <strong className="text-foreground">刷新一次</strong>才会变回简体
              —— 简繁不是一一对应，转回去会出错，所以只停止继续转。
            </p>
          </div>
        </Panel>

        <Panel title="货币" hint="这个站用哪种货币计价。">
          <div className="space-y-3 p-5">
            {CURRENCIES.map((c) => (
              <Choice
                key={c.key}
                active={cur.currency === c.key}
                onClick={() => patch({ currency: c.key })}
              >
                <span className="min-w-0">
                  <span className="block text-[14px]">{c.name}</span>
                  <span className="block text-[12px] text-muted-foreground">{c.hint}</span>
                </span>
              </Choice>
            ))}
            {/*
              🔴 这句话必须摆在按钮旁边。运营多半以为切一下就能"换算成台币"，
                 而它只换符号 —— 不说清楚的话，他会以为 ¥3280 的商品变成了 NT$3280
                 是系统算好的汇率。
            */}
            <p className="text-[12px] text-muted-foreground">
              🔴 <strong className="text-foreground">只换符号，不折算金额。</strong>
              ¥3280 切过去就是 NT$3280，不是按汇率换算的结果 ——
              这个开关是在声明"库里的价本来就是这种货币"。
            </p>
          </div>
        </Panel>
      </div>

      {save.error && <ErrorBlock error={save.error} />}
    </>
  );
}

// ═══════════════════════════ 价格规则与限价 ═══════════════════════════

interface RuleRow {
  id: number;
  scope: string;
  scopeValue: string;
  categoryId: number | null;
  mode: string;
  value: string;
  note: string;
  active: boolean;
}
interface LimitRow {
  id: number;
  scope: string;
  scopeValue: string;
  categoryId: number | null;
  minMarkupRate: string | null;
  maxMarkupRate: string | null;
  belowCostPolicy: string;
  note: string;
  active: boolean;
}

const SCOPE_LABEL: Record<string, string> = {
  platform: "全平台",
  org_type: "按身份",
  level: "按等级",
  org: "指定商家",
};
const MODE_LABEL: Record<string, string> = {
  markup_rate: "加价率",
  markup_fixed: "固定加价",
  absolute: "指定售价",
};
const POLICY_LABEL: Record<string, string> = {
  allow: "放行",
  warn: "提示但放行",
  block: "直接拦下",
};

export function PriceRulesTab() {
  const qc = useQueryClient();
  const rules = useQuery({
    queryKey: ["admin", "price-rules"],
    queryFn: () => api.get<{ items: RuleRow[] }>("/admin/price-rules"),
  });
  const limits = useQuery({
    queryKey: ["admin", "price-limits"],
    queryFn: () => api.get<{ items: LimitRow[] }>("/admin/price-limits"),
  });
  const cats = useQuery({
    queryKey: ["admin", "categories"],
    queryFn: () => api.get<{ items: { id: number; name: string }[] }>("/admin/categories"),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["admin", "price-rules"] });
    void qc.invalidateQueries({ queryKey: ["admin", "price-limits"] });
    /** 🔴 规则一改，前台每个人看到的价都会变 —— 商品缓存必须作废。 */
    void qc.invalidateQueries({ queryKey: ["products"] });
  };

  const [addingRule, setAddingRule] = useState(false);
  const [rule, setRule] = useState({
    scope: "platform",
    scopeValue: "",
    categoryId: "",
    mode: "markup_rate",
    /** ⚠️ 界面上填的是**百分数**（15 = 加 15%），存的是小数 0.15。 */
    percent: "15",
    note: "",
  });

  const createRule = useMutation({
    mutationFn: () =>
      api.post("/admin/price-rules", {
        scope: rule.scope,
        scopeValue: rule.scope === "platform" ? "" : rule.scopeValue.trim(),
        categoryId: rule.categoryId ? Number(rule.categoryId) : null,
        mode: rule.mode,
        value: rule.mode === "markup_rate" ? Number(rule.percent) / 100 : Number(rule.percent),
        note: rule.note.trim(),
      }),
    onSuccess: () => {
      setAddingRule(false);
      invalidate();
    },
  });
  const toggleRule = useMutation({
    mutationFn: (r: RuleRow) => api.patch(`/admin/price-rules/${r.id}`, { active: !r.active }),
    onSuccess: invalidate,
  });
  const delRule = useMutation({
    mutationFn: (id: number) => api.del(`/admin/price-rules/${id}`),
    onSuccess: invalidate,
  });
  const patchLimit = useMutation({
    mutationFn: (v: { id: number; body: Record<string, unknown> }) =>
      api.patch(`/admin/price-limits/${v.id}`, v.body),
    onSuccess: invalidate,
  });

  const catName = (id: number | null) =>
    id ? (cats.data?.items.find((c) => c.id === id)?.name ?? `#${id}`) : "全部商品";

  if (rules.isPending || limits.isPending) return <Spinner compact />;

  return (
    <>

      <Panel
        title="价格规则"
        hint="按身份 / 等级 / 品类给一个默认加价率。商家在「我的定价」里单独改过的规格不受它影响。"
        action={
          !addingRule && (
            <Button size="sm" onClick={() => setAddingRule(true)}>
              <Plus className="size-3.5" />
              新增规则
            </Button>
          )
        }
      >
        {addingRule && (
          <div className="mb-4 rounded-lg border border-brand-200 p-4">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <div>
                <span className="mb-1.5 block text-[13px] text-muted-foreground">作用范围</span>
                <select
                  className="h-11 w-full rounded-lg border border-border bg-background px-3 text-[15px]"
                  value={rule.scope}
                  onChange={(e) => setRule({ ...rule, scope: e.target.value, scopeValue: "" })}
                >
                  {Object.entries(SCOPE_LABEL).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>

              {rule.scope !== "platform" && (
                <Input
                  label={
                    rule.scope === "org_type"
                      ? "身份（dealer / distributor）"
                      : rule.scope === "level"
                        ? "等级标识"
                        : "组织 id"
                  }
                  value={rule.scopeValue}
                  onChange={(e) => setRule({ ...rule, scopeValue: e.target.value })}
                />
              )}

              <div>
                <span className="mb-1.5 block text-[13px] text-muted-foreground">限定品类</span>
                <select
                  className="h-11 w-full rounded-lg border border-border bg-background px-3 text-[15px]"
                  value={rule.categoryId}
                  onChange={(e) => setRule({ ...rule, categoryId: e.target.value })}
                >
                  <option value="">全部商品</option>
                  {(cats.data?.items ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <Input
                /* ⚠️ 界面上填百分数，存的是小数 —— 让运营填 0.15 必然有人填成 15。 */
                label="加价率（%）"
                inputMode="decimal"
                value={rule.percent}
                onChange={(e) => setRule({ ...rule, percent: e.target.value })}
              />
            </div>

            <div className="mt-4">
              <Input
                label="备注（人话，例如「双十一瓷砖统一降价」）"
                value={rule.note}
                onChange={(e) => setRule({ ...rule, note: e.target.value })}
              />
            </div>

            {createRule.error && <ErrorBlock error={createRule.error} />}
            <div className="mt-4 flex gap-2">
              <Button loading={createRule.isPending} onClick={() => createRule.mutate()}>
                保存
              </Button>
              <Button variant="ghost" onClick={() => setAddingRule(false)}>
                取消
              </Button>
            </div>
          </div>
        )}

        {rules.data!.items.length === 0 ? (
          <Empty
            title="还没有平台级价格规则"
            hint="没有规则时按系统默认加价率算；商家自己设的加价率不受影响"
            compact
          />
        ) : (
          <Table head={["作用范围", "限定品类", "方式", "数值", "备注", "状态", "操作"]}>
            {rules.data!.items.map((r) => (
              <Tr key={r.id}>
                <Td>
                  {SCOPE_LABEL[r.scope] ?? r.scope}
                  {r.scopeValue && (
                    <span className="ml-1.5 font-mono text-[11px] text-muted-foreground">
                      {r.scopeValue}
                    </span>
                  )}
                </Td>
                <Td className="text-muted-foreground">{catName(r.categoryId)}</Td>
                <Td className="text-muted-foreground">{MODE_LABEL[r.mode] ?? r.mode}</Td>
                <Td className="tabular-nums">
                  {r.mode === "markup_rate"
                    ? `${(Number(r.value) * 100).toFixed(2)}%`
                    : fen(Number(r.value))}
                </Td>
                <Td className="text-muted-foreground">{r.note || "—"}</Td>
                <Td>{r.active ? <Badge tone="ok">生效中</Badge> : <Badge tone="mute">已停用</Badge>}</Td>
                <Td>
                  <span className="flex gap-3 text-[12px]">
                    <button
                      className="text-muted-foreground hover:text-foreground"
                      onClick={() => toggleRule.mutate(r)}
                    >
                      {r.active ? "停用" : "启用"}
                    </button>
                    <button
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => delRule.mutate(r.id)}
                    >
                      删除
                    </button>
                  </span>
                </Td>
              </Tr>
            ))}
          </Table>
        )}
      </Panel>

      <div className="mt-6">
        <Panel
          title="限价"
          hint="商家能把价压到多低。🔴 最低加价率 0 = 不得低于进货价；「亏本怎么办」决定是放行、提示还是直接拦下。"
        >
          {limits.data!.items.length === 0 ? (
            <Empty title="还没有限价规则" hint="没有规则时按系统默认：亏本只提示不拦" compact />
          ) : (
            <Table head={["作用范围", "限定品类", "最低加价率", "最高加价率", "亏本怎么办", "状态"]}>
              {limits.data!.items.map((l) => (
                <Tr key={l.id}>
                  <Td>
                    {SCOPE_LABEL[l.scope] ?? l.scope}
                    {l.scopeValue && (
                      <span className="ml-1.5 font-mono text-[11px] text-muted-foreground">
                        {l.scopeValue}
                      </span>
                    )}
                  </Td>
                  <Td className="text-muted-foreground">{catName(l.categoryId)}</Td>
                  <Td className="tabular-nums">
                    {l.minMarkupRate == null ? "不限" : `${(Number(l.minMarkupRate) * 100).toFixed(2)}%`}
                  </Td>
                  <Td className="tabular-nums">
                    {l.maxMarkupRate == null ? "不限" : `${(Number(l.maxMarkupRate) * 100).toFixed(2)}%`}
                  </Td>
                  <Td>
                    {/* ⚠️ 这一项直接决定商家能不能亏本卖，做成下拉当场改。 */}
                    <select
                      className="h-8 rounded-md border border-border bg-background px-2 text-[13px]"
                      value={l.belowCostPolicy}
                      onChange={(e) =>
                        patchLimit.mutate({ id: l.id, body: { belowCostPolicy: e.target.value } })
                      }
                    >
                      {Object.entries(POLICY_LABEL).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </Td>
                  <Td>{l.active ? <Badge tone="ok">生效中</Badge> : <Badge tone="mute">已停用</Badge>}</Td>
                </Tr>
              ))}
            </Table>
          )}
        </Panel>
      </div>
      {(toggleRule.error || delRule.error || patchLimit.error) && (
        <ErrorBlock error={toggleRule.error ?? delRule.error ?? patchLimit.error} />
      )}
    </>
  );
}

// ═══════════════════════════ 用户 ═══════════════════════════

interface UserRow {
  id: number;
  phone: string | null;
  email: string | null;
  nickname: string;
  disabled: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  /** 客户的专属服务商（注册时用邀请码定的）。⚠️ 商家自己没有这个。 */
  binding: { orgId: number; orgName: string | null } | null;
  orgs: {
    orgId: number;
    orgName: string | null;
    orgType: string | null;
    /** 组织树上的上级。⚠️ 平台是根节点，它是 null。 */
    parentOrgId: number | null;
    parentOrgName: string | null;
  }[];
  orderCount: number;
}

/** 新建 / 编辑用户的表单。空 = 关着。`id` 为空 = 新建。 */
interface UserDraft {
  id: number | null;
  phone: string;
  nickname: string;
  email: string;
  password: string;
  bindOrgId: number | "";
  reason: string;
}

const EMPTY_DRAFT: UserDraft = {
  id: null,
  phone: "",
  nickname: "",
  email: "",
  password: "",
  bindOrgId: "",
  reason: "",
};

export function UsersTab() {
  const qc = useQueryClient();
  const [keyword, setKeyword] = useState("");
  const [onlyDisabled, setOnlyDisabled] = useState<"" | "true" | "false">("");
  const [ask, setAsk] = useState<UserRow | null>(null);
  const [draft, setDraft] = useState<UserDraft | null>(null);
  const [toDelete, setToDelete] = useState<UserRow | null>(null);

  /**
   * ⚠️ **搜索要防抖。** keyword 直接进 queryKey 的话，每敲一个字就是一次
   *    查库 —— 输一个 11 位手机号就打 11 次请求，而只有最后一次的结果有用。
   *    250ms 是"打完一个字还在想下一个字"的间隔，人感觉不出延迟。
   */
  const [kwInput, setKwInput] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setKeyword(kwInput.trim()), 250);
    return () => clearTimeout(t);
  }, [kwInput]);

  const q = useQuery({
    queryKey: ["admin", "user-list", keyword, onlyDisabled],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (keyword) qs.set("keyword", keyword);
      if (onlyDisabled) qs.set("disabled", onlyDisabled);
      return api.get<{ items: UserRow[]; nextCursor: string | null }>(`/admin/user-list?${qs}`);
    },
  });

  /** 建号时要选「归到哪个商家名下」，所以要拿节点列表。 */
  const orgList = useQuery({
    queryKey: ["admin", "orgs"],
    queryFn: () => api.get<{ id: number; name: string; type: string }[]>("/admin/orgs"),
    /** ⚠️ 新建要选归属、编辑要选上级，两态都得有。 */
    enabled: Boolean(draft),
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin", "user-list"] });

  const setDisabled = useMutation({
    mutationFn: (v: { id: number; disabled: boolean; reason: string }) =>
      api.post(`/admin/users/${v.id}/disabled`, { disabled: v.disabled, reason: v.reason }),
    onSuccess: () => {
      setAsk(null);
      void refresh();
    },
  });

  /**
   * 🔴 **新建和保存是两条路，不能合成一个 upsert。**
   *    新建必须有密码（没密码的号他登不上），改资料时密码是**选填**且
   *    一旦填了就是"重置密码 + 踢掉所有设备"—— 两者的必填项和后果都不一样。
   */
  const save = useMutation({
    mutationFn: (d: UserDraft) => {
      if (d.id === null) {
        return api.post("/admin/users", {
          phone: d.phone.trim(),
          password: d.password,
          ...(d.nickname.trim() ? { nickname: d.nickname.trim() } : {}),
          ...(d.email.trim() ? { email: d.email.trim() } : {}),
          ...(d.bindOrgId ? { bindOrgId: Number(d.bindOrgId) } : {}),
          ...(d.reason.trim() ? { reason: d.reason.trim() } : {}),
        });
      }
      return api.patch(`/admin/users/${d.id}`, {
        phone: d.phone.trim(),
        nickname: d.nickname.trim(),
        email: d.email.trim(),
        ...(d.password ? { password: d.password } : {}),
        ...(d.reason.trim() ? { reason: d.reason.trim() } : {}),
      });
    },
    onSuccess: () => {
      setDraft(null);
      void refresh();
    },
  });

  const del = useMutation({
    mutationFn: (v: { id: number; reason: string }) =>
      api.del(`/admin/users/${v.id}`, { reason: v.reason }),
    onSuccess: () => {
      setToDelete(null);
      void refresh();
    },
  });

  /**
   * 🔴 **赋予身份 = 给他挂一个组织节点并让他当 owner。**
   *    这个平台里"一个人能干什么"是**他与组织的关系**，不是 users 表上的字段
   *    （那张表里根本没有 role 这一列）。以前后台没有这个入口 ——
   *    要让某人成为供应商，只能去数据库手插两张表。
   */
  const [grant, setGrant] = useState<{
    type: "supplier" | "dealer" | "distributor";
    parentOrgId: number | "";
    name: string;
    reason: string;
  } | null>(null);

  const grantMut = useMutation({
    mutationFn: (v: { userId: number; body: Record<string, unknown> }) =>
      api.post(`/admin/users/${v.userId}/identities`, v.body),
    onSuccess: () => {
      setGrant(null);
      setDraft(null);
      void refresh();
      /** ⚠️ 组织树那份缓存也要作废 —— 刚挂上去的节点得在树里看得见。 */
      void qc.invalidateQueries({ queryKey: ["admin", "orgs"] });
    },
  });

  const revokeMut = useMutation({
    mutationFn: (v: { userId: number; orgId: number }) =>
      api.del(`/admin/users/${v.userId}/identities/${v.orgId}`),
    onSuccess: () => {
      setDraft(null);
      void refresh();
    },
  });

  return (
    <>

      <div className="mb-4 flex items-center gap-4">
        <div className="flex gap-1.5">
          {[
            { k: "" as const, label: "全部" },
            { k: "false" as const, label: "正常" },
            { k: "true" as const, label: "已禁用" },
          ].map((t) => (
            <button
              key={t.k || "all"}
              onClick={() => setOnlyDisabled(t.k)}
              className={cn(
                "rounded-lg px-3 py-1.5 text-[13px] transition-colors",
                onlyDisabled === t.k
                  ? "bg-brand-600 text-white"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground",
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative ml-auto w-[280px]">
          <Search className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="h-9 pl-10"
            placeholder="搜手机号 / 昵称 / 邮箱（尾号也认）"
            value={kwInput}
            onChange={(e) => setKwInput(e.target.value)}
          />
        </div>
        <Button size="sm" onClick={() => setDraft({ ...EMPTY_DRAFT })}>
          新增用户
        </Button>
      </div>

      {q.isPending ? (
        <Spinner compact />
      ) : q.error ? (
        <ErrorBlock error={q.error} />
      ) : q.data.items.length === 0 ? (
        <Empty
          title="没有符合条件的用户"
          compact
          hint={keyword || onlyDisabled ? "换个关键词，或者清空筛选看全部。" : undefined}
          action={
            keyword || onlyDisabled
              ? {
                  label: "清空筛选",
                  onClick: () => {
                    setKwInput("");
                    setOnlyDisabled("");
                  },
                }
              : undefined
          }
        />
      ) : (
        <Table head={["用户", "手机号", "专属服务商", "商家身份", "订单", "注册时间", "状态", "操作"]}>
          {q.data.items.map((u) => (
            <Tr key={u.id}>
              <Td>{u.nickname || "（未设昵称）"}</Td>
              <Td className="font-mono tabular-nums">{u.phone ?? "—"}</Td>
              {/*
                🔴 **商家账号的"归属"是组织树上的上级，不是客户绑定。**
                   老板：「陈总是没有归属的，但是像东鹏就是陈总的下级，应该归属平台」。

                   两件不同的事，以前只显示前者，于是每个商家都写着"无归属"：
                   · 客户 → 看 `binding`（注册时用邀请码定的专属服务商）
                   · 商家 → 看**他那个节点的上级**（东鹏 → 平台，王师傅 → 经销商A）
                   · 平台 → 它是根，**本来就没有上级**，"无归属"在这儿是对的

                ⚠️ 标一行小字说清楚这个数是从哪来的 —— 两种来源显示在同一列里，
                   不标的话运营分不出"他是谁的客户"和"他挂在谁下面"。
              */}
              <Td className="text-muted-foreground">
                {u.binding?.orgName ? (
                  u.binding.orgName
                ) : u.orgs[0]?.parentOrgName ? (
                  <>
                    <span className="block text-foreground">{u.orgs[0].parentOrgName}</span>
                    <span className="text-[11px]">组织树上级</span>
                  </>
                ) : u.orgs[0]?.orgType === "platform" ? (
                  /** ⚠️ 平台是根，说"无归属"会让人以为数据丢了。 */
                  <span className="text-[12px]">平台自身（根节点）</span>
                ) : (
                  "无归属"
                )}
              </Td>
              <Td className="text-muted-foreground">
                {u.orgs.length ? u.orgs.map((o) => o.orgName).join(" / ") : "—"}
              </Td>
              <Td className="tabular-nums">{u.orderCount}</Td>
              <Td className="text-muted-foreground">
                {new Date(u.createdAt).toLocaleDateString("zh-CN")}
              </Td>
              <Td>{u.disabled ? <Badge tone="bad">已禁用</Badge> : <Badge tone="ok">正常</Badge>}</Td>
              <Td>
                <RowActions>
                  <RowAction
                    onClick={() =>
                      setDraft({
                        ...EMPTY_DRAFT,
                        id: u.id,
                        phone: u.phone ?? "",
                        nickname: u.nickname,
                        email: u.email ?? "",
                      })
                    }
                  >
                    编辑
                  </RowAction>
                  <RowAction tone={u.disabled ? "primary" : "danger"} onClick={() => setAsk(u)}>
                    {u.disabled ? "解禁" : "禁用"}
                  </RowAction>
                  {/*
                    🔴 **有订单的、是商家的不给删** —— 服务端会拒（那才是最后一道），
                       但按钮先灰掉：让他点下去再读一段报错，是在浪费他一次点击。
                       `title` 说清为什么灰的，否则他只会以为页面坏了。
                  */}
                  <RowAction
                    tone="danger"
                    disabled={u.orderCount > 0 || u.orgs.length > 0}
                    title={
                      u.orderCount > 0
                        ? `他下过 ${u.orderCount} 笔单，删了那些单就没有下单人了 —— 请改用「禁用」`
                        : u.orgs.length > 0
                          ? "他是商家的成员，账上可能有钱、名下可能有客户 —— 要停这家店请去组织树"
                          : "删了找不回来"
                    }
                    onClick={() => setToDelete(u)}
                  >
                    删除
                  </RowAction>
                </RowActions>
              </Td>
            </Tr>
          ))}
        </Table>
      )}

      <ReasonDialog
        open={Boolean(ask)}
        title={ask?.disabled ? "解禁这个账号" : "禁用这个账号"}
        description={
          ask?.disabled
            ? "解禁后他可以正常登录和下单。理由会通过站内信发给他。"
            : "🔴 禁用之后他登不上、也下不了单。已经下过的单和名下的钱都不动。理由会通过站内信发给他 —— 不写清楚他只会以为网站坏了。"
        }
        confirmLabel={ask?.disabled ? "确认解禁" : "确认禁用"}
        danger={!ask?.disabled}
        loading={setDisabled.isPending}
        onConfirm={(reason) =>
          ask && setDisabled.mutate({ id: ask.id, disabled: !ask.disabled, reason })
        }
        onClose={() => setAsk(null)}
      />
      {setDisabled.error && <ErrorBlock error={setDisabled.error} />}

      {/* ── 新建 / 编辑 ── */}
      <Dialog open={Boolean(draft)} onOpenChange={(v) => !v && setDraft(null)}>
        {/*
          ⚠️ **这个框会长过一屏**（展开「赋予身份」之后有十来个字段），
             不封高度的话底部的「开通身份 / 保存」两颗按钮会掉到视口外面，
             而对话框本身不跟着页面滚 —— 表现是填完了却按不到提交。
        */}
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{draft?.id === null ? "新增用户" : "修改用户"}</DialogTitle>
            <DialogDescription>
              {draft?.id === null
                ? "代客户开一个商城账号。开完会给他发一条站内信说明账号的来路 —— 不说的话，他收到一个能登录的号只会以为信息被泄露了。"
                : "改手机号或重置密码会把他所有设备踢下线（这两件事的场景基本都是账号出了问题）。"}
            </DialogDescription>
          </DialogHeader>

          {draft && (
            <div className="space-y-3">
              {/*
                🔴 **跟注册页用同一个组件（带地区选择）。**
                   老板：「平台管理后台的新增用户手机号还是 11 位，
                   但是登录页却可以选择不同地区，这个对不上」。

                   后端的 `adminCreateUserSchema` 早就收 `memberPhoneSchema`
                   （任意地区、存 E.164），是这个输入框一直写死在"11 位"上 ——
                   于是运营给一个台湾客户代开号会被打回来，而报错说的是
                   "手机号格式不对"，他只会以为是自己打错了。

                ⚠️ 两处必须是**同一个组件**而不是各写一份：号码规则
                   （哪些地区、什么格式算合法）只能有一份，
                   抄两份迟早出现"注册页填得过、后台填不进去"。
              */}
              <PhoneInput
                value={draft.phone}
                onChange={(e164) => setDraft({ ...draft, phone: e164 })}
              />
              <Input
                label="昵称（选填）"
                value={draft.nickname}
                onChange={(e) => setDraft({ ...draft, nickname: e.target.value })}
              />
              <Input
                label="邮箱（选填）"
                value={draft.email}
                onChange={(e) => setDraft({ ...draft, email: e.target.value })}
              />
              <div>
                <Input
                  label={draft.id === null ? "初始密码" : "重置密码（不填就不改）"}
                  type="password"
                  placeholder="至少 8 位"
                  value={draft.password}
                  onChange={(e) => setDraft({ ...draft, password: e.target.value })}
                />
                {draft.id === null && (
                  <p className="mt-1 text-[12px] text-muted-foreground">
                    这个密码要你亲口告诉他 —— 系统不会把密码发出去。
                  </p>
                )}
              </div>

              {/*
                🔴 **归属在建号时就要定。** 这个平台里"用户注册那一刻确定的归属"
                   是核心资产：它决定他看到的是哪个价、售后归谁。
                   建完再转移是可以的，但转移**只改以后不改过去**。
                ⚠️ 只在新建时出：改归属是另一件事，有专门的「客户归属」页
                   （那里要填理由、记变更日志、纠纷时是唯一依据）。
              */}
              {draft.id === null && (
                <div>
                  <span className="mb-1.5 block text-[13px] text-muted-foreground">
                    归到哪个商家名下（选填）
                  </span>
                  <select
                    className="h-10 w-full rounded-lg border border-border bg-background px-3 text-[14px]"
                    value={String(draft.bindOrgId)}
                    onChange={(e) =>
                      setDraft({ ...draft, bindOrgId: e.target.value ? Number(e.target.value) : "" })
                    }
                  >
                    <option value="">不指定（无归属，按平台指导零售价买）</option>
                    {(orgList.data ?? [])
                      .filter((o) => o.type !== "supplier")
                      .map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                  </select>
                </div>
              )}

              <Input
                label="备注 / 理由（选填）"
                placeholder="为什么开这个号 / 为什么改"
                value={draft.reason}
                onChange={(e) => setDraft({ ...draft, reason: e.target.value })}
              />

              {/*
                ══════════ 商家身份 ══════════

                🔴 **这才是"账号可编辑"的正事** —— 赋予身份。
                   一个人在这个平台里能干什么，取决于他和哪些组织有关系：

                   | 身份     | 买家 | 卖家 | 新增商品 |
                   |----------|------|------|---------|
                   | 一般客户 | ✓    | —    | —       |
                   | 经销商   | ✓    | ✓    | ✗       |
                   | 分销商   | ✓    | ✓    | ✓       |
                   | 供应商   | ✓    | ✓    | ✓       |

                ⚠️ 只在**编辑**时出：新建时用户还没落库，没有 id 可挂。
                   开完号点一下「编辑」就能给身份。
              */}
              {draft.id !== null && (
                <div className="rounded-lg border border-border p-3">
                  <p className="mb-1 text-[13px] font-medium">商家身份</p>
                  <p className="mb-2.5 text-[12px] leading-relaxed text-muted-foreground">
                    给他挂一个店 / 供应商节点，他就多一个「我是卖家」的身份。
                    经销商和分销商<strong>挂在谁下面决定了他的进货价</strong>，别挂错。
                  </p>

                  {(() => {
                    const row = q.data?.items.find((u) => u.id === draft.id);
                    if (!row?.orgs.length) {
                      return (
                        <p className="mb-2.5 text-[12px] text-muted-foreground">
                          他现在只是买家，没有任何经营身份。
                        </p>
                      );
                    }
                    return (
                      <ul className="mb-2.5 space-y-1.5">
                        {row.orgs.map((o) => (
                          <li
                            key={o.orgId}
                            className="flex items-center justify-between rounded-md bg-secondary/50 px-2.5 py-1.5 text-[13px]"
                          >
                            <span>{o.orgName}</span>
                            {/*
                              🔴 **收回身份只解除"谁在经营它"，不动这家店本身。**
                                 店还在、货还在、账上的钱还在、下级还挂着。
                                 要停掉这家店走组织树的停业 / 清退。
                              ⚠️ 组织的 owner 摘不掉（服务端会拒）——
                                 摘了这家店就成了无主的：没人能登进去改价、发货、提现。
                            */}
                            <button
                              className="text-[12px] text-destructive hover:underline"
                              onClick={() =>
                                revokeMut.mutate({ userId: draft.id!, orgId: o.orgId })
                              }
                            >
                              收回
                            </button>
                          </li>
                        ))}
                      </ul>
                    );
                  })()}

                  {grant ? (
                    <div className="space-y-2.5">
                      <div>
                        <span className="mb-1.5 block text-[13px] text-muted-foreground">
                          给他什么身份
                        </span>
                        <select
                          className="h-10 w-full rounded-lg border border-border bg-background px-3 text-[14px]"
                          value={grant.type}
                          onChange={(e) =>
                            setGrant({ ...grant, type: e.target.value as typeof grant.type })
                          }
                        >
                          <option value="supplier">供应商 —— 供货给平台，能上架商品</option>
                          <option value="dealer">经销商 —— 卖上游供的货，不能上架商品</option>
                          <option value="distributor">分销商 —— 卖上游供的货，也能上架自己的货</option>
                        </select>
                      </div>
                      <Input
                        label="店铺 / 公司名"
                        placeholder="例如：东鹏建材供应商"
                        value={grant.name}
                        onChange={(e) => setGrant({ ...grant, name: e.target.value })}
                      />
                      <div>
                        <span className="mb-1.5 block text-[13px] text-muted-foreground">
                          挂在谁下面
                        </span>
                        <select
                          className="h-10 w-full rounded-lg border border-border bg-background px-3 text-[14px]"
                          value={String(grant.parentOrgId)}
                          onChange={(e) =>
                            setGrant({
                              ...grant,
                              parentOrgId: e.target.value ? Number(e.target.value) : "",
                            })
                          }
                        >
                          <option value="">请选择上级</option>
                          {(orgList.data ?? [])
                            /** ⚠️ 供应商只能挂平台：他是链条外的供货方，不参与分销层级。 */
                            .filter((o) =>
                              grant.type === "supplier"
                                ? o.type === "platform"
                                : o.type !== "supplier",
                            )
                            .map((o) => (
                              <option key={o.id} value={o.id}>
                                {o.name}
                              </option>
                            ))}
                        </select>
                        <p className="mt-1 text-[12px] text-muted-foreground">
                          {grant.type === "supplier"
                            ? "供应商供货给平台，挂在平台下面。"
                            : "🔴 他的进货价 = 上级给他的价。挂错了以后只能转移，而转移只改以后不改过去。"}
                        </p>
                      </div>
                      <Input
                        label="理由（选填）"
                        placeholder="为什么给他这个身份"
                        value={grant.reason}
                        onChange={(e) => setGrant({ ...grant, reason: e.target.value })}
                      />
                      {grantMut.error && <ErrorBlock error={grantMut.error} />}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          loading={grantMut.isPending}
                          disabled={grant.name.trim().length < 2 || !grant.parentOrgId}
                          onClick={() =>
                            grantMut.mutate({
                              userId: draft.id!,
                              body: {
                                type: grant.type,
                                parentOrgId: Number(grant.parentOrgId),
                                name: grant.name.trim(),
                                ...(grant.reason.trim() ? { reason: grant.reason.trim() } : {}),
                              },
                            })
                          }
                        >
                          开通身份
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setGrant(null)}>
                          取消
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setGrant({ type: "distributor", parentOrgId: "", name: "", reason: "" })
                      }
                    >
                      赋予商家身份
                    </Button>
                  )}
                  {revokeMut.error && <ErrorBlock error={revokeMut.error} />}
                </div>
              )}

              {save.error && <ErrorBlock error={save.error} />}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDraft(null)}>
              取消
            </Button>
            <Button
              loading={save.isPending}
              disabled={
                !draft ||
                /*
                  🔴 **号码合不合法由 `phoneProblem` 判，不要在这儿写正则。**
                     原来写的是 `/^1[3-9]\d{9}$/` —— 只认大陆号，
                     于是换成带地区选择的输入框之后，填台湾号「开通」永远是灰的，
                     而界面上没有任何东西说明为什么。
                  ⚠️ 规则只能有一份（`@zx/contracts` 的 `phone.ts`），
                     前端写一份、后端再写一份，两份迟早对不上，
                     症状是"这儿填得过、提交上去被打回来"。
                */
                phoneProblem(draft.phone) !== null ||
                (draft.id === null && draft.password.length < 8) ||
                (draft.password.length > 0 && draft.password.length < 8)
              }
              onClick={() => draft && save.mutate(draft)}
            >
              {draft?.id === null ? "开通" : "保存"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── 删除 ── */}
      <ReasonDialog
        open={Boolean(toDelete)}
        title="删除这个账号"
        description="🔴 删了就找不回来了：他的地址、站内信、归属记录会一起没掉。只有没下过单、也不是任何商家成员的账号才允许删 —— 别的请用「禁用」。这条理由是唯一的痕迹。"
        confirmLabel="确认删除"
        danger
        loading={del.isPending}
        onConfirm={(reason) => toDelete && del.mutate({ id: toDelete.id, reason })}
        onClose={() => setToDelete(null)}
      />
      {del.error && <ErrorBlock error={del.error} />}
    </>
  );
}

// ═══════════════════════════ 公告 ═══════════════════════════

export function AnnouncementTab() {
  const [form, setForm] = useState({
    audience: "all" as "all" | "merchants",
    title: "",
    body: "",
    link: "",
  });
  const send = useMutation({
    mutationFn: () =>
      api.post<{ sent: number; truncated: boolean }>("/admin/announcements", {
        audience: form.audience,
        title: form.title.trim(),
        body: form.body.trim(),
        link: form.link.trim() || undefined,
      }),
  });

  return (
    <>

      <Panel title="发一条公告">
        <div className="max-w-[640px] space-y-4">
          <div>
            <span className="mb-1.5 block text-[13px] text-muted-foreground">发给谁</span>
            <div className="flex gap-2">
              {[
                { k: "all" as const, label: "所有用户", hint: "含买家" },
                { k: "merchants" as const, label: "只发商家", hint: "所有店铺的成员" },
              ].map((a) => (
                <button
                  key={a.k}
                  onClick={() => setForm({ ...form, audience: a.k })}
                  className={cn(
                    "flex-1 rounded-lg border px-4 py-3 text-left transition-colors",
                    form.audience === a.k
                      ? "border-brand-500 bg-brand-50"
                      : "border-border hover:border-brand-300",
                  )}
                >
                  <span className="block text-[14px] font-medium">{a.label}</span>
                  <span className="block text-[12px] text-muted-foreground">{a.hint}</span>
                </button>
              ))}
            </div>
          </div>

          <Input
            label="标题"
            placeholder="例如：春节期间物流安排"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <div>
            <span className="mb-1.5 block text-[13px] text-muted-foreground">正文</span>
            <textarea
              rows={4}
              className="w-full resize-none rounded-lg border border-border bg-background px-3 py-2.5 text-[14px] leading-relaxed outline-none focus:border-brand-500"
              placeholder="写清楚时间、影响范围、要对方做什么"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
            />
          </div>
          {/* ⚠️ 每条消息都该"能点进去做点什么" —— 只通知不给路，等于只说了一半。 */}
          <Input
            label="点进去落到哪一页（选填，例如 /orders）"
            placeholder="/orders"
            value={form.link}
            onChange={(e) => setForm({ ...form, link: e.target.value })}
          />

          {send.error && <ErrorBlock error={send.error} />}
          {send.isSuccess && (
            <p className="rounded-lg bg-emerald-50 px-4 py-3 text-[13px] text-emerald-800">
              已发给 {send.data.sent} 个人
              {send.data.truncated && "（超过一次上限，只发了前一批）"}。
            </p>
          )}

          <Button
            loading={send.isPending}
            disabled={!form.title.trim()}
            onClick={() => send.mutate()}
          >
            <Megaphone className="size-4" />
            发出去
          </Button>
        </div>
      </Panel>
    </>
  );
}

// ═══════════════════════════ 系统健康 ═══════════════════════════

interface ErrorRow {
  id: number;
  kind: string;
  message: string;
  count: number;
  lastAt: string;
  resolvedAt: string | null;
}

export function HealthTab() {
  const qc = useQueryClient();
  const errors = useQuery({
    queryKey: ["admin", "errors"],
    queryFn: () =>
      api.get<{ items: ErrorRow[]; summary: { kinds: number; total: number; since: string } }>(
        "/admin/errors?resolved=false",
      ),
  });
  const backups = useQuery({
    queryKey: ["admin", "backups"],
    queryFn: () =>
      api.get<{
        lastOkAt: string | null;
        lastOkSize: number;
        hoursSinceLastOk: number | null;
        stale: boolean;
        failuresLast7d: number;
        dir: string;
      }>("/admin/backups"),
  });
  const media = useQuery({
    queryKey: ["admin", "media"],
    queryFn: () => api.get<{ scope: string; files: number; bytes: number }[]>("/admin/media/usage"),
  });

  const resolve = useMutation({
    mutationFn: (ids: number[]) => api.post("/admin/errors/resolve", { ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "errors"] }),
  });
  const runBackup = useMutation({
    mutationFn: () => api.post("/admin/backups/run", {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "backups"] }),
  });

  const mb = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(1)} MB`;

  return (
    <>

      <div className="space-y-6">
        <Panel
          title="数据库备份"
          hint="备份不跑的时候不会报错，只会什么都不发生 —— 要盯的是「上次成功是什么时候」。"
          action={
            <Button size="sm" variant="outline" loading={runBackup.isPending} onClick={() => runBackup.mutate()}>
              立刻备一份
            </Button>
          }
        >
          {backups.isPending ? (
            <Spinner compact />
          ) : backups.error ? (
            <ErrorBlock error={backups.error} />
          ) : (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <Stat label="上次成功">
                {backups.data!.lastOkAt
                  ? new Date(backups.data!.lastOkAt).toLocaleString("zh-CN")
                  : "从来没成功过"}
              </Stat>
              <Stat label="距今">
                {backups.data!.hoursSinceLastOk == null
                  ? "—"
                  : `${backups.data!.hoursSinceLastOk.toFixed(1)} 小时`}
              </Stat>
              <Stat label="上次大小">
                {backups.data!.lastOkSize ? mb(backups.data!.lastOkSize) : "—"}
              </Stat>
              <Stat label="近 7 天失败" danger={backups.data!.failuresLast7d > 0}>
                {backups.data!.failuresLast7d}
              </Stat>
            </div>
          )}
          {runBackup.error && <ErrorBlock error={runBackup.error} />}
        </Panel>

        <Panel
          title="未处理的错误"
          hint="同一类错误会合并计数。处理完点「标为已处理」，下次就不再出现在这张表里。"
          action={
            (errors.data?.items.length ?? 0) > 0 && (
              <Button
                size="sm"
                variant="outline"
                loading={resolve.isPending}
                onClick={() => resolve.mutate(errors.data!.items.map((e) => e.id))}
              >
                全部标为已处理
              </Button>
            )
          }
        >
          {errors.isPending ? (
            <Spinner compact />
          ) : errors.error ? (
            <ErrorBlock error={errors.error} />
          ) : errors.data.items.length === 0 ? (
            <Empty title="没有未处理的错误" hint="真出事的时候这里会有东西" compact />
          ) : (
            <Table head={["类型", "信息", "次数", "最后一次", ""]}>
              {errors.data.items.map((e) => (
                <Tr key={e.id}>
                  <Td className="font-mono text-[12px]">{e.kind}</Td>
                  <Td className="max-w-[520px] truncate">{e.message}</Td>
                  <Td className="tabular-nums">{e.count}</Td>
                  <Td className="text-muted-foreground">
                    {new Date(e.lastAt).toLocaleString("zh-CN")}
                  </Td>
                  <Td>
                    <button
                      className="text-[12px] text-brand-600 hover:underline"
                      onClick={() => resolve.mutate([e.id])}
                    >
                      标为已处理
                    </button>
                  </Td>
                </Tr>
              ))}
            </Table>
          )}
        </Panel>

        <Panel title="图片占用" hint="没人引用的图片由定时任务定期清理。">
          {media.isPending ? (
            <Spinner compact />
          ) : media.error ? (
            <ErrorBlock error={media.error} />
          ) : (media.data?.length ?? 0) === 0 ? (
            <Empty title="还没有上传过图片" compact />
          ) : (
            <Table head={["用途", "文件数", "占用"]}>
              {media.data!.map((m) => (
                <Tr key={m.scope}>
                  <Td>{m.scope}</Td>
                  <Td className="tabular-nums">{m.files}</Td>
                  <Td className="tabular-nums">{mb(m.bytes)}</Td>
                </Tr>
              ))}
            </Table>
          )}
        </Panel>
      </div>
    </>
  );
}

function Stat({
  label,
  children,
  danger,
}: {
  label: string;
  children: React.ReactNode;
  danger?: boolean;
}) {
  return (
    <div className="rounded-lg border border-border px-4 py-3">
      <p className="text-[12px] text-muted-foreground">{label}</p>
      <p className={cn("mt-1 text-[15px]", danger && "text-destructive")}>{children}</p>
    </div>
  );
}
